import { memo } from 'react';

import type { Location } from 'history';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { compose } from 'recompact';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { errorBoundary } from 'src/app/hocs';
import {
  AccountPageInsurance,
  AccountPagePhone,
  AccountPagePhoneUpdateSuccess,
  checkoutAddons,
  loggedInQuestions,
  loggedInTransferAppInterstitialPrompt,
  loggedInTransferPostOrderPrompt,
  loggedInTransferRx,
  medicationDetails,
  medications,
  orderTracking,
  partnerOnboardingFlow,
  rescheduleOrder,
  shop,
  shop2,
  shop2Category,
  tellYourDoctor,
  welcome,
} from 'src/app/router/paths';
import {
  fetchOrderHistory,
  segmentTrack,
  setNewArchivedPageModalShown,
  setPageAlert,
  setShowNewArchivedPageModal,
} from 'src/app/state/actions';
import {
  getBFFOrder,
  getHasMedications,
  getIsNewArchivedPageModalBeingShown,
  getIsUserPartOfCapsuleAtWork,
  getNewArchivedPageModalShown,
  getNumPendingTransfers,
  getOnboardingCurrentIndex,
  getOnboardingProgressFillPercent,
  getOnboardingStepsLength,
  isLoggedIn,
} from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { OrderItem } from 'src/app/types';
import { arePathnamesEqual } from 'src/app/utils';

import { LoggedInRenderer } from '.';

const formatRefrigerationNames = (names: string[]) => {
  if (names.length === 1) {
    return names[0];
  } else if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  } else if (names.length > 2) {
    // Handling when it is drug 1, drug 2, and drug 3.
    // Last entry needs to be prefixed by and.
    const lastName = `and ${names[names.length - 1]}`;
    names[names.length - 1] = lastName;
    return names.join(', ');
  }

  return;
};

type OwnProps = RouteComponentProps & {
  match: {
    params: {
      ndc: string;
      orderId: string;
      prescriptionId: string;
      shortCodeId: string;
    };
  };
};

function makeMapStateToProps() {
  return (state: RootState, ownProps: OwnProps) => {
    const isUserLoggedIn = isLoggedIn(state);

    const currentPath = ownProps.match.url;
    const filledInPath = orderTracking.toPath(ownProps.match.params);
    const inOrderTracking = arePathnamesEqual(currentPath, filledInPath);
    const isInMedicationsPath = arePathnamesEqual(currentPath, medications.toPath());
    const isInShopPath = arePathnamesEqual(currentPath, shop.toPath());
    const isInEssentialsPath = arePathnamesEqual(currentPath, shop2.toPath());
    const isInEssentialsCategoryPath = arePathnamesEqual(currentPath, shop2Category.toPath());
    const isInAddInsurance = arePathnamesEqual(currentPath, AccountPageInsurance.toPath());
    const isInWelcomePath = arePathnamesEqual(currentPath, welcome.toPath());
    const isInPartnerOnboardingPath = arePathnamesEqual(currentPath, partnerOnboardingFlow.toPath());
    const isInHealthProfilePath = currentPath.startsWith('/rx/health_profile/');
    const isInFAQPath = arePathnamesEqual(currentPath, loggedInQuestions.toPath());
    const isInUpdatePhonePath = arePathnamesEqual(currentPath, AccountPagePhone.toPath());
    const isInUpdatePhoneSuccessPath = arePathnamesEqual(currentPath, AccountPagePhoneUpdateSuccess.toPath());
    const isUpsellEssentialsRoute = arePathnamesEqual(currentPath, checkoutAddons.route);
    const isRescheduleOrderFromRepeatFailure = arePathnamesEqual(
      currentPath,
      rescheduleOrder.toPath(ownProps.match.params)
    );
    const isPostOrderTransferPrompt = arePathnamesEqual(currentPath, loggedInTransferPostOrderPrompt.route);
    const isPostOrderTransferInterstitial = arePathnamesEqual(currentPath, loggedInTransferAppInterstitialPrompt.route);
    const isTellYourDoctorInterstitial = arePathnamesEqual(currentPath, tellYourDoctor.toPath());

    const isLoggedInTransferPath = arePathnamesEqual(currentPath, loggedInTransferRx.toPath());
    const showHorizontalProgressBar = isLoggedInTransferPath;

    const isInMedicationDetailsPath = arePathnamesEqual(currentPath, medicationDetails.toPath(ownProps.match.params));
    const isHealthProfileUpdatePath = currentPath.startsWith('/rx/health_profile/');
    // Product only wants the earliest shown order from orders that are arriving today and tomorrow.
    const orderId = ownProps.match.params.orderId;
    const order = getBFFOrder(state, orderId);
    const orderItems = order?.items;

    // Grabs all of the medication names that need refrigeration. Then applies some formatting
    // for consumer readability.
    const refrigerationNames = orderItems?.reduce((accumulator: string[], orderItem: OrderItem) => {
      const refrigerationRequired = orderItem?.isRefrigerationRequired;
      if (refrigerationRequired) {
        accumulator.push(orderItem?.name);
      }
      return accumulator;
    }, []);

    const refrigerationMessage =
      refrigerationNames?.length > 0 ? `${formatRefrigerationNames(refrigerationNames)} requires refrigeration` : null;

    const showTabbedFooterBar =
      isInMedicationsPath ||
      isInShopPath ||
      isInEssentialsPath ||
      isInEssentialsCategoryPath ||
      isInMedicationDetailsPath;

    // No side nav... No marketing styles... No embedded gutter styles...
    const isMinimalStyleLayout =
      isRescheduleOrderFromRepeatFailure ||
      isPostOrderTransferPrompt ||
      isPostOrderTransferInterstitial ||
      isTellYourDoctorInterstitial;

    const isFauxLoggedInLayout =
      isMinimalStyleLayout ||
      isInWelcomePath ||
      isInFAQPath ||
      isLoggedInTransferPath ||
      isInPartnerOnboardingPath ||
      isInUpdatePhonePath ||
      isInUpdatePhoneSuccessPath ||
      isHealthProfileUpdatePath ||
      isUpsellEssentialsRoute;

    const isFullWidthLoggedInLayout = isInEssentialsPath || isInEssentialsCategoryPath;

    const isNewArchivedPageModalShown =
      getIsNewArchivedPageModalBeingShown(state) && !getNewArchivedPageModalShown(state);
    const currentStepIndex = getOnboardingCurrentIndex(state);
    const progressBarFillPercent = getOnboardingProgressFillPercent(state);
    const progressBarTotalSteps = getOnboardingStepsLength(state);

    const numPendingMeds = getNumPendingTransfers(state);

    const hasNoMedicationHistory = !getHasMedications(state) && numPendingMeds === 0;

    return {
      //agreedToTermsVersion,
      currentPath,
      hideNavBar: (state.ui as any).navBar.hideNavBar,
      hideNavLinks: (state.ui as any).navBar.hideNavLinks,
      hasNoMedicationHistory,
      inOrderTracking,
      isInAddInsurance,
      isMinimalStyleLayout,
      isUpsellEssentialsRoute,
      isFauxLoggedInLayout,
      isFullWidthLoggedInLayout,
      isInMedicationsPath,
      isInShopPath,
      isInEssentialsPath,
      isInEssentialsCategoryPath,
      isLoggedIn: isUserLoggedIn,
      isSimpleNav: isInWelcomePath,
      isInWelcomePath,
      isInPartnerOnboardingPath,
      isInHealthProfilePath,
      isUserPartOfCapsuleAtWork: getIsUserPartOfCapsuleAtWork(state),
      refrigerationMessage,
      showTabbedFooterBar,
      showHorizontalProgressBar,
      isLoggedInTransferPath,
      isNewArchivedPageModalShown,
      currentStepIndex,
      progressBarFillPercent,
      progressBarTotalSteps,
    };
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    dispatchSetPageAlert: (type: null) => dispatch(setPageAlert(type)),
    dispatchTransferRxButtonClick: () => dispatch(segmentTrack(UNIFIED.viewTransferForm, { location: 'Menu' })),
    dispatchGetOrderHistory: () => dispatch(fetchOrderHistory()),
    dispatchSetNewArchivedPageModalShown: () => dispatch(setNewArchivedPageModalShown()),
    dispatchSetShowNewArchivedPageModal: (showModal: boolean) => dispatch(setShowNewArchivedPageModal(showModal)),
  };
}

const connector = connect(makeMapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export interface LoggedInProps extends PropsFromRedux {
  hasFixedNav: boolean;
  location: Location;
}

export const LoggedIn = compose(errorBoundary, connector, memo)(LoggedInRenderer);

LoggedIn.displayName = LoggedInRenderer.displayName;
